import React, { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { TooltipProvider } from '@components/ui/tooltip'
import { ChevronLeft, ChevronRight, LayoutDashboard, Image, Globe, Star, Key, CreditCard } from 'lucide-react'
import { useSidebar } from './SidebarProvider'
import { SidebarItem } from './SidebarItem'

const Sidebar = React.memo(({ isChat = false }) => {
  const { sideMenuVisible, toggleSidebar } = useSidebar()

  const SIDEBAR_PAGES = [
    { route: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { route: 'media', label: 'Media', icon: Image },
    { route: 'dashboard', key: 'public', query: 'public', queryValue: 'true', label: 'Public', icon: Globe },
    { route: 'favorites', label: 'Favorites', icon: Star },
    { route: 'api-keys', label: 'API Keys', icon: Key },
    { route: 'billing', label: 'Billing', icon: CreditCard },
  ]

  const isActivePage = useMemo(() => {
    return (item) => {
      if (typeof window === 'undefined') return false
      const { pathname, search } = window.location
      const urlParams = new URLSearchParams(search)
      const currentPath = pathname.replace(/^\/+/, '') // Remove leading slashes

      if (item.query) {
        return currentPath === item.route && urlParams.get(item.query) === item.queryValue
      }

      return currentPath === item.route && !urlParams.has('public')
    }
  }, [])

  return (
    <TooltipProvider>
      <aside
        className={cn(
          'flex flex-col h-screen bg-white border-r border-gray-100 shadow-sm transition-all duration-300 ease-in-out z-[99]',
          sideMenuVisible ? 'w-[240px]' : 'w-[70px]',
          !isChat ? 'fixed' : 'relative'
        )}
      >
        <button
          onClick={toggleSidebar}
          className={cn(
            'flex absolute top-4 -right-3 justify-center items-center w-7 h-7 rounded-full',
            'bg-white text-gray-800 shadow-md transition-all duration-200 hover:bg-gray-50',
            'border border-gray-200 outline-none focus:outline-none'
          )}
          aria-label={sideMenuVisible ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {sideMenuVisible ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>

        <nav className="overflow-y-auto flex-1 px-3 py-0">
          <div className="space-y-1 pt-4">
            {SIDEBAR_PAGES.map((item, i) => (
              <SidebarItem
                key={`dash-${i}`}
                item={item}
                isActive={isActivePage(item)}
                sideMenuVisible={sideMenuVisible}
              />
            ))}
          </div>
        </nav>
      </aside>
    </TooltipProvider>
  )
})

Sidebar.displayName = 'Sidebar'

export default Sidebar
